/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */
import { useCallback, useEffect } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { LegacyEquity, ProductType, SynthOIEquity } from '../../types';
import { dayjs, ONE_HOUR_MS, prevBusinessDay } from '../../util';
import useLog from '../useLog';
import poll from '../../util/poll';
import {
  workerState,
  equitiesFetchLoadingState,
  unpricedEquitiesState,
  legacyEquitiesState,
} from '../../states';
import useAuth from '../auth/useAuth';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useSynthOi from './useSynthOi';

const POLL_INTERVAL = 4 * ONE_HOUR_MS;
const EQUITIES_QUERY_KEY = 'equities';

const createEquitiesURL = (
  isSynthOI: boolean,
  hasScannersAccess: boolean,
  date: string | null = prevBusinessDay().format('YYYY-MM-DD'),
) => {
  const url = isSynthOI
    ? `synth_oi/v1/${hasScannersAccess ? '' : 'free_'}equities`
    : hasScannersAccess
      ? 'v4/equities'
      : 'v1/free_equities';
  return date != null && isSynthOI ? `${url}?date=${date}` : url;
};

const useEquities = () => {
  const worker = useRecoilValue(workerState);
  const setUnpricedEquities = useSetRecoilState(unpricedEquitiesState);
  const setLegacyEquities = useSetRecoilState(legacyEquitiesState);
  const setEquitiesLoading = useSetRecoilState(equitiesFetchLoadingState);
  const { hasAccessToProduct } = useAuth();
  const hasEhAccess = hasAccessToProduct(ProductType.EQUITYHUB);
  const { fetchAPIWithLog } = useLog('useEquities');
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');
  const queryClient = useQueryClient();

  const { isSynthOI } = useSynthOi();

  const convertDataToMap = (
    data: (SynthOIEquity | LegacyEquity)[],
  ): Map<string, LegacyEquity | SynthOIEquity> => {
    if (isSynthOI) {
      return new Map(
        data.map((e: SynthOIEquity | LegacyEquity) => [
          e.sym,
          e as SynthOIEquity,
        ]),
      );
    } else {
      return new Map(
        data.map((e: SynthOIEquity | LegacyEquity) => [
          e.sym,
          e as LegacyEquity,
        ]),
      );
    }
  };

  const getEquitiesUrl = useCallback(() => {
    return createEquitiesURL(
      isSynthOI,
      hasEhAccess,
      dayjs(date).format('YYYY-MM-DD'),
    );
  }, [isSynthOI, hasEhAccess, date]);

  // Use React Query to fetch equities
  const equitiesQuery = useQuery({
    queryKey: [EQUITIES_QUERY_KEY, isSynthOI, date, hasEhAccess],
    queryFn: async () => {
      try {
        const url = getEquitiesUrl();
        const data =
          await fetchAPIWithLog<(SynthOIEquity | LegacyEquity)[]>(url);
        return data;
      } catch (error) {
        console.error('Failed to fetch equities:', error);
        throw error;
      }
    },
    staleTime: POLL_INTERVAL / 2,
    refetchInterval: POLL_INTERVAL,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    enabled: true,
    initialDataUpdatedAt: 0, // Ensure first load is fresh
  });

  // Update loading state based on query status
  useEffect(() => {
    setEquitiesLoading(equitiesQuery.isLoading || equitiesQuery.isFetching);
  }, [equitiesQuery.isFetching, equitiesQuery.isLoading]);

  // Use effect to process the data when it changes
  useEffect(() => {
    const data = queryClient.getQueryData([
      EQUITIES_QUERY_KEY,
      isSynthOI,
      date,
      hasEhAccess,
    ]) as (LegacyEquity | SynthOIEquity)[];
    if (data && Array.isArray(data) && data.length > 0) {
      const equitiesMap = convertDataToMap(data);
      setUnpricedEquities(equitiesMap);
    }
  }, [
    queryClient.getQueryState([
      EQUITIES_QUERY_KEY,
      isSynthOI,
      date,
      hasEhAccess,
    ])?.dataUpdateCount,
    isSynthOI,
    date,
    hasEhAccess,
    setUnpricedEquities,
    queryClient,
  ]);

  // Keep legacy equities always available for Total OI charts.
  // When in Legacy mode, reuse the main query data. When in SynthOI mode,
  // fetch legacy equities separately (cached, low frequency).
  const legacyEquitiesQuery = useQuery({
    // hasEhAccess is part of the key so logging in mid-session refetches the
    // full v4 data instead of reusing the cached free_equities response.
    queryKey: [EQUITIES_QUERY_KEY, false, null, hasEhAccess],
    queryFn: async () => {
      const url = createEquitiesURL(false, hasEhAccess);
      return await fetchAPIWithLog<LegacyEquity[]>(url);
    },
    staleTime: POLL_INTERVAL,
    refetchOnWindowFocus: false,
    enabled: isSynthOI, // only fetch when in SynthOI mode
  });

  useEffect(() => {
    if (!isSynthOI) {
      // In Legacy mode, the main query already has legacy data
      const data = queryClient.getQueryData<LegacyEquity[]>([
        EQUITIES_QUERY_KEY,
        false,
        date,
        hasEhAccess,
      ]);
      if (data && data.length > 0) {
        setLegacyEquities(new Map(data.map((e) => [e.sym, e])));
      }
    } else if (
      legacyEquitiesQuery.data &&
      legacyEquitiesQuery.data.length > 0
    ) {
      setLegacyEquities(
        new Map(legacyEquitiesQuery.data.map((e) => [e.sym, e])),
      );
    }
  }, [
    isSynthOI,
    legacyEquitiesQuery.data,
    queryClient.getQueryState([EQUITIES_QUERY_KEY, false, date, hasEhAccess])
      ?.dataUpdateCount,
    hasEhAccess,
    setLegacyEquities,
  ]);

  // Function to fetch equities data directly
  const fetchEquities = useCallback(
    async (
      isSynthOI: boolean,
      customDate?: string,
      overrideAccess?: boolean,
    ): Promise<Map<string, LegacyEquity | SynthOIEquity>> => {
      try {
        const url = createEquitiesURL(
          isSynthOI,
          overrideAccess ?? hasEhAccess,
          customDate
            ? dayjs(customDate).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD'),
        );
        const data =
          await fetchAPIWithLog<(LegacyEquity | SynthOIEquity)[]>(url);
        return new Map(
          data.map((e) => [
            e.sym,
            isSynthOI ? (e as SynthOIEquity) : (e as LegacyEquity),
          ]),
        );
      } catch (error) {
        console.error('Failed to fetch equities:', error);
        throw error;
      }
    },
    [hasEhAccess, fetchAPIWithLog],
  );

  // Outputs an object with keys being 'sym' and value being the 'name' of equity
  const getSymbols = useCallback(async () => {
    return await fetchAPIWithLog<Record<string, string>>(
      `${isSynthOI ? 'synth_oi/' : ''}v1/eh_symbols`,
    );
  }, [isSynthOI, fetchAPIWithLog]);

  const getEquityScanners = useCallback(
    async (isSynthOI: boolean) => {
      const suffix = hasEhAccess
        ? 'v1/equityScanners'
        : 'v1/free_equityScanners';
      const url = `${isSynthOI ? 'synth_oi/' : ''}${suffix}`;
      return await fetchAPIWithLog(url);
    },
    [hasEhAccess, fetchAPIWithLog],
  );

  const handlePollResponse = ({ json }: any, requestedURL: string) => {
    if ((json?.length ?? 0) === 0) {
      return;
    }
    const currentURL = createEquitiesURL(isSynthOI, hasEhAccess);
    if (requestedURL !== currentURL) {
      console.log(`synth-oi: Ignoring stale result from ${requestedURL}`);
      return;
    }

    const equitiesMap = convertDataToMap(json);
    setUnpricedEquities(equitiesMap);
    setEquitiesLoading(false);
  };

  const setupEquitiesPoller = useCallback(() => {
    return poll(
      worker,
      {
        url: getEquitiesUrl(),
        interval: POLL_INTERVAL,
        onResponse: handlePollResponse,
      },
      { maxRetries: 1 },
    );
  }, [worker, getEquitiesUrl]);

  const refetchEquities = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [EQUITIES_QUERY_KEY, isSynthOI, date],
    });
  }, [queryClient, isSynthOI, date]);

  return {
    getSymbols,
    getEquityScanners,
    setupEquitiesPoller,
    refetchEquities,
    fetchEquities,
  };
};

export default useEquities;
